import React, { useState, useEffect, useRef } from 'react';
import { getVehicleBrand, formatVehicleBrandModel } from '../../utils/vehicleBrand';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Badge,
  Flex,
  Heading,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Collapse,
  IconButton,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Accordion,
} from '@chakra-ui/react';
import { FiEye, FiChevronDown, FiChevronUp, FiCalendar, FiUser, FiTool, FiDollarSign, FiClock, FiFileText } from 'react-icons/fi';
import api from '../../services/api';
import { getData, postData } from '../../utils/httpClient';
import useBcuUsdRate from '../../hooks/useBcuUsdRate';
import { formatMoneyDual } from '../../utils/currencyDisplay';
import { formatDateOnlyLong, formatDateOnlyDisplay, formatServiceDateLong } from '../../utils/dateOnly';
import SchedulerCollapsibleSection from '../Scheduler/SchedulerCollapsibleSection';

const VehicleHistoryModalComplete = ({ 
  isOpen, 
  onClose, 
  vehicleData, 
  isLoading,
  kilometersLabel = 'Kilometraje',
}) => {
  const toast = useToast();
  const [expandedServices, setExpandedServices] = useState({});
  const [servicePdfDocs, setServicePdfDocs] = useState({});
  const [openingPdfId, setOpeningPdfId] = useState(null);
  const [resolvedVehicle, setResolvedVehicle] = useState(vehicleData);
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const bodyScrollRef = useRef(null);
  const { rate: usdRate } = useBcuUsdRate();

  // Siempre refrescar el vehículo al abrir: el listado paginado no trae historiales
  useEffect(() => {
    if (!isOpen) return;

    const vehicleId = vehicleData?.id;
    if (!vehicleId) {
      setResolvedVehicle(vehicleData || null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingVehicle(true);
      try {
        const response = await getData(`/vehicles/${vehicleId}`, {
          skipLoader: true,
        });
        if (!cancelled) {
          setResolvedVehicle(response?.payload?.vehicle || vehicleData);
        }
      } catch (error) {
        console.error('Error al refrescar historial del vehículo:', error);
        if (!cancelled) {
          setResolvedVehicle(vehicleData || null);
        }
      } finally {
        if (!cancelled) {
          setLoadingVehicle(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, vehicleData?.id]);

  const displayVehicle = resolvedVehicle || vehicleData;
  const showLoading = Boolean(isLoading || loadingVehicle);

  // Función helper para extraer texto de cualquier valor de forma segura
  const safeText = (value, fallback = 'N/A') => {
    if (!value) return fallback;
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (typeof value === 'object' && value !== null) {
      // Intentar diferentes propiedades comunes
      return value.name || value.description || value.title || fallback;
    }
    return fallback;
  };

  const formatCurrency = (amount, currencyCode = 'UYU') => {
    if (amount == null || amount === '' || isNaN(Number(amount))) return 'N/A';
    return formatMoneyDual(amount, currencyCode, usdRate);
  };

  // Función para toggle de servicios expandidos
  const toggleServiceExpansion = (serviceIndex) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceIndex]: !prev[serviceIndex]
    }));
  };

  useEffect(() => {
    if (!isOpen || !displayVehicle?.vehicleServices?.length) {
      setServicePdfDocs({});
      return;
    }

    let cancelled = false;
    const loadPdfDocs = async () => {
      const entries = await Promise.all(
        displayVehicle.vehicleServices.map(async (service) => {
          if (!service?.id) return [null, null];
          try {
            const res = await getData(
              `/vehicleServices/${service.id}/pdf-documents`,
              { skipLoader: true },
            );
            return [
              service.id,
              {
                receptionDocumentId: res?.payload?.receptionDocumentId || null,
                serviceDocumentId: res?.payload?.serviceDocumentId || null,
                warrantyDocumentId: res?.payload?.warrantyDocumentId || null,
              },
            ];
          } catch {
            return [
              service.id,
              { receptionDocumentId: null, serviceDocumentId: null, warrantyDocumentId: null },
            ];
          }
        }),
      );
      if (cancelled) return;
      const map = {};
      entries.forEach(([id, docs]) => {
        if (id) map[id] = docs;
      });
      setServicePdfDocs(map);
    };

    loadPdfDocs();
    return () => {
      cancelled = true;
    };
  }, [isOpen, displayVehicle]);

  // Rueda del mouse: prioriza scroll de tablas/listas internas; si no, el body del modal
  useEffect(() => {
    if (!isOpen) return undefined;
    const el = bodyScrollRef.current;
    if (!el) return undefined;

    const canScrollY = (node) => {
      if (!(node instanceof HTMLElement) || node === el) return false;
      if (node.scrollHeight <= node.clientHeight + 1) return false;
      const { overflowY } = window.getComputedStyle(node);
      return (
        overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowY === 'overlay' ||
        // TableContainer de Chakra fuerza overflowY:hidden pero igual scrollea
        (overflowY === 'hidden' && node.dataset?.scrollable === 'true')
      );
    };

    const onWheel = (e) => {
      if (e.ctrlKey) return;

      let node = e.target;
      while (node && node !== el) {
        if (canScrollY(node)) {
          const atTop = node.scrollTop <= 0;
          const atBottom =
            node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
          const goingUp = e.deltaY < 0;
          const goingDown = e.deltaY > 0;
          if ((goingUp && !atTop) || (goingDown && !atBottom)) {
            node.scrollTop += e.deltaY;
            e.preventDefault();
            e.stopPropagation();
            return;
          }
        }
        node = node.parentElement;
      }

      el.scrollTop += e.deltaY;
      e.preventDefault();
    };

    el.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => {
      el.removeEventListener('wheel', onWheel, { capture: true });
    };
  }, [isOpen, showLoading, displayVehicle]);

  const openPdfByDocumentId = async (documentId, label) => {
    if (!documentId) return;
    setOpeningPdfId(documentId);
    try {
      const response = await getData(`/document/${documentId}`, {
        skipLoader: true,
      });
      const document = response?.payload?.document;
      if (!document?.file) {
        throw new Error('Documento vacío');
      }
      const pdfData = new Uint8Array(document.file.data);
      const blob = new Blob([pdfData], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (error) {
      toast({
        title: `${label || 'PDF'} no disponible`,
        description: error?.message || 'No se pudo abrir el documento.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setOpeningPdfId(null);
    }
  };

  const regenerateAndOpenPdf = async (serviceId, kind, label) => {
    if (!serviceId) return;
    const loadingKey = `${kind}-${serviceId}`;
    setOpeningPdfId(loadingKey);
    try {
      const endpoint =
        kind === 'reception'
          ? `/vehicleServices/${serviceId}/reception-pdf`
          : kind === 'warranty'
            ? `/vehicleServices/${serviceId}/warranty-pdf`
            : `/vehicleServices/${serviceId}/service-pdf`;
      const response = await postData(endpoint, {}, { skipLoader: true });
      const docId =
        response?.payload?.documentId ||
        response?.payload?.vehicleService?.documentId;
      if (!docId) {
        throw new Error('No se obtuvo el documento generado.');
      }
      setServicePdfDocs((prev) => {
        const current = prev[serviceId] || {};
        if (kind === 'reception') {
          return {
            ...prev,
            [serviceId]: { ...current, receptionDocumentId: docId },
          };
        }
        if (kind === 'warranty') {
          return {
            ...prev,
            [serviceId]: { ...current, warrantyDocumentId: docId },
          };
        }
        return {
          ...prev,
          [serviceId]: { ...current, serviceDocumentId: docId },
        };
      });
      await openPdfByDocumentId(docId, label);
    } catch (error) {
      toast({
        title: `${label || 'PDF'} no disponible`,
        description:
          error?.response?.data?.message ||
          error?.message ||
          'No se pudo generar el documento.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setOpeningPdfId(null);
    }
  };

  const handleGeneratePDF = async () => {
    try {
      console.log('Solicitando PDF para vehículo:', displayVehicle.id);
      
      const response = await api.post(`/document/vehicle-history/${displayVehicle.id}`, displayVehicle, {
        responseType: 'arraybuffer', // Cambiar a arraybuffer para mejor manejo de PDF
      });

      console.log('Respuesta recibida, tamaño:', response.data.byteLength);

      // Crear un blob con la respuesta y tipo MIME específico
      const blob = new Blob([response.data], { 
        type: 'application/pdf'
      });
      
      console.log('Blob creado, tamaño:', blob.size);
      
      // Verificar que el blob tiene contenido
      if (blob.size === 0) {
        throw new Error('El PDF generado está vacío');
      }
      
      // Crear un URL temporal para el blob
      const url = window.URL.createObjectURL(blob);
      console.log('URL del blob creada:', url);
      
      // Abrir el PDF en una nueva pestaña
      const newWindow = window.open(url, '_blank');
      
      // Verificar si la ventana se abrió correctamente
      if (!newWindow) {
        // Si no se puede abrir nueva ventana, intentar descarga
        console.log('No se pudo abrir nueva ventana, intentando descarga...');
        const link = document.createElement('a');
        link.href = url;
        link.download = `historial_vehiculo_${displayVehicle.plate || displayVehicle.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast({
          title: 'PDF Descargado',
          description: 'No se pudo abrir en nueva pestaña, se ha descargado el archivo',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'PDF Generado',
          description: 'El historial del vehículo se está abriendo en una nueva pestaña',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      
      // Limpiar el URL después de un tiempo
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000); // Aumentar tiempo para dar chance de que cargue

    } catch (error) {
      console.error('Error generando PDF:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message || 'No se pudo generar el PDF del historial del vehículo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (showLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Historial de Vehículo</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box p={8} textAlign="center">
              <Text fontSize="lg">Cargando historial del vehículo...</Text>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (!displayVehicle) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Historial de Vehículo</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box p={8} textAlign="center">
              <Text fontSize="lg" color="red.500">
                No se encontraron datos del vehículo
              </Text>
              <Button mt={4} onClick={onClose}>
                Cerrar
              </Button>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="6xl"
      scrollBehavior="inside"
      blockScrollOnMount={false}
     closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>
          <Flex justify="space-between" align="center" pr={8}>
            <VStack align="start" spacing={1}>
              <Heading size="lg">Historial de Vehículo</Heading>
              <Text fontSize="md" color="gray.600" fontWeight="normal">
                {safeText(formatVehicleBrandModel(displayVehicle))} ({safeText(displayVehicle.year)}) - {safeText(displayVehicle.plate)}
              </Text>
            </VStack>
            <Button
              leftIcon={<FiEye />}
              colorScheme="gray"
              variant="solid"
              onClick={handleGeneratePDF}
              size="md"
            >
              Ver Historia en PDF
            </Button>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody
          ref={bodyScrollRef}
          pb={6}
          flex="1"
          minH={0}
          overflowY="auto"
          overscrollBehavior="contain"
          sx={{ WebkitOverflowScrolling: 'touch' }}
        >
          <Tabs variant="enclosed" colorScheme="gray">
            <TabList>
              <Tab>Información General</Tab>
              <Tab>Historial de Servicios ({displayVehicle.vehicleServices?.length || 0})</Tab>
              <Tab>Historial de Clientes ({displayVehicle.vehicleCustomerHistories?.length || 0})</Tab>
            </TabList>

            <TabPanels>
              {/* Tab 1: Información General */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  <Box p={6} borderWidth="1px" borderRadius="lg" bg="gray.50" borderColor="gray.200">
                    <Heading size="md" mb={4} color="blue.700">📋 Información del Vehículo</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="semibold">Marca</Text>
                        <Text fontWeight="bold" fontSize="lg">{safeText(getVehicleBrand(displayVehicle))}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="semibold">Modelo</Text>
                        <Text fontWeight="bold" fontSize="lg">{safeText(displayVehicle.model?.description || displayVehicle.model)}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="semibold">Año</Text>
                        <Text fontWeight="bold" fontSize="lg">{safeText(displayVehicle.year)}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="semibold">Placa</Text>
                        <Text fontWeight="bold" fontSize="lg">{safeText(displayVehicle.plate)}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="semibold">Chasis</Text>
                        <Text fontWeight="bold" fontSize="lg">{safeText(displayVehicle.chassisNumber)}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="semibold">Color</Text>
                        <Text fontWeight="bold" fontSize="lg">{safeText(displayVehicle.color)}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="semibold">Motor</Text>
                        <Text fontWeight="bold" fontSize="lg">{safeText(displayVehicle.engineNumber)}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="semibold">{kilometersLabel}</Text>
                        <Text fontWeight="bold" fontSize="lg">{displayVehicle.kilometers ? `${displayVehicle.kilometers.toLocaleString()} km` : 'N/A'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="semibold">Combustible</Text>
                        <Text fontWeight="bold" fontSize="lg">{safeText(displayVehicle.fuelType)}</Text>
                      </Box>
                    </SimpleGrid>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Tab 2: Historial de Servicios */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  {displayVehicle.vehicleServices && displayVehicle.vehicleServices.length > 0 ? (
                    <>
                      <Box textAlign="center" mb={4}>
                        <Badge colorScheme="gray" fontSize="lg" p={2}>
                          {displayVehicle.vehicleServices.length} servicios registrados
                        </Badge>
                      </Box>
                      
                      {[...(displayVehicle.vehicleServices || [])]
                        .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
                        .map((service, index) => (
                          <Box
                            key={`service-${index}`}
                            borderWidth="1px"
                            borderRadius="lg"
                            bg="gray.50"
                            borderColor="gray.200"
                            shadow="sm"
                          >
                            {/* Header del servicio - siempre visible */}
                            <Box 
                              p={4} 
                              bg="gray.100" 
                              cursor="pointer"
                              onClick={() => toggleServiceExpansion(index)}
                              _hover={{ bg: "gray.150" }}
                              transition="background 0.2s"
                            >
                              <HStack justify="space-between">
                                <HStack spacing={4}>
                                  <Box>
                                    <Text fontWeight="bold" fontSize="lg" color="gray.800">
                                      {safeText(service.description, 'Servicio sin descripción')}
                                    </Text>
                                    <HStack spacing={4} mt={1} flexWrap="wrap">
                                      <HStack>
                                        <FiCalendar />
                                        <Text fontSize="sm" color="gray.600">
                                          {formatServiceDateLong(service, 'Sin fecha')}
                                        </Text>
                                      </HStack>
                                      <Badge colorScheme="gray" size="sm">
                                        {safeText(
                                          service.status?.description ||
                                            service.status?.name ||
                                            service.status ||
                                            (Array.isArray(service.agenda) &&
                                              service.agenda.find((a) => a?.status)
                                                ?.status),
                                          'Sin estado',
                                        )}
                                      </Badge>
                                    </HStack>
                                    <HStack mt={2} spacing={2} flexWrap="wrap">
                                      {(() => {
                                        const isWarranty =
                                          Number(service.serviceTypeId) === 2 ||
                                          String(
                                            service.serviceType?.name || '',
                                          )
                                            .toLowerCase()
                                            .includes('garant');
                                        return (
                                          <>
                                            {!isWarranty && (
                                              <Button
                                                size="xs"
                                                leftIcon={<FiFileText />}
                                                colorScheme="blue"
                                                variant="outline"
                                                isLoading={
                                                  openingPdfId ===
                                                  `reception-${service.id}`
                                                }
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  regenerateAndOpenPdf(
                                                    service.id,
                                                    'reception',
                                                    'Orden de trabajo / recepción',
                                                  );
                                                }}
                                              >
                                                Orden de recepción
                                              </Button>
                                            )}
                                            <Button
                                              size="xs"
                                              leftIcon={<FiFileText />}
                                              colorScheme="green"
                                              variant="outline"
                                              isLoading={
                                                openingPdfId ===
                                                `service-${service.id}`
                                              }
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                regenerateAndOpenPdf(
                                                  service.id,
                                                  'service',
                                                  'Informe de servicio',
                                                );
                                              }}
                                            >
                                              Informe de servicio
                                            </Button>
                                          </>
                                        );
                                      })()}
                                    </HStack>
                                  </Box>
                                </HStack>
                                
                                <IconButton
                                  icon={expandedServices[index] ? <FiChevronUp /> : <FiChevronDown />}
                                  variant="ghost"
                                  colorScheme="gray"
                                  aria-label={expandedServices[index] ? "Contraer" : "Expandir"}
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleServiceExpansion(index);
                                  }}
                                />
                              </HStack>
                            </Box>

                            {/* Contenido expandible con toda la información */}
                            <Collapse
                              in={expandedServices[index]}
                              style={{ overflow: 'visible' }}
                            >
                              <Box p={5} bg="white">
                                <VStack spacing={5} align="stretch">
                                  
                                  {/* Información básica del servicio */}
                                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                                    <Box>
                                      <HStack mb={2}>
                                        <FiUser />
                                        <Text fontWeight="semibold" color="gray.700">Mecánico Principal</Text>
                                      </HStack>
                                      <Text fontWeight="bold">{safeText(service.mechanic?.name, 'No asignado')}</Text>
                                      {service.mechanic?.phone && (
                                        <Text fontSize="sm" color="gray.600">📞 {service.mechanic.phone}</Text>
                                      )}
                                    </Box>

                                    <Box>
                                      <HStack mb={2}>
                                        <FiUser />
                                        <Text fontWeight="semibold" color="gray.700">Asistente</Text>
                                      </HStack>
                                      <Text>{safeText(service.assistant?.name, 'Sin asistente')}</Text>
                                      {service.assistant?.phone && (
                                        <Text fontSize="sm" color="gray.600">📞 {service.assistant.phone}</Text>
                                      )}
                                    </Box>

                                    <Box>
                                      <HStack mb={2}>
                                        <FiClock />
                                        <Text fontWeight="semibold" color="gray.700">Horario</Text>
                                      </HStack>
                                      <Text>{service.time || 'No especificado'}</Text>
                                      <Text fontSize="sm" color="gray.600">
                                        Total: {service.totalTime || 0} min
                                      </Text>
                                    </Box>

                                    <Box>
                                      <HStack mb={2}>
                                        <FiDollarSign />
                                        <Text fontWeight="semibold" color="gray.700">Costo Volkswagen</Text>
                                      </HStack>
                                      <Text fontWeight="bold" color="blue.600">
                                        {service.volkswagenCost ? formatCurrency(service.volkswagenCost, 'UYU') : 'No especificado'}
                                      </Text>
                                    </Box>

                                    <Box>
                                      <HStack mb={2}>
                                        <FiTool />
                                        <Text fontWeight="semibold" color="gray.700">Tipo de Servicio</Text>
                                      </HStack>
                                      <Text>{safeText(service.serviceName, 'No especificado')}</Text>
                                    </Box>

                                    <Box>
                                      <HStack mb={2}>
                                        <FiCalendar />
                                        <Text fontWeight="semibold" color="gray.700">Odómetro</Text>
                                      </HStack>
                                      <Text>{service.odometer ? `${service.odometer} km` : 'No registrado'}</Text>
                                    </Box>
                                  </SimpleGrid>

                                  <Divider />

                                  {/* Fechas de inicio y fin */}
                                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    <Box>
                                      <Text fontWeight="semibold" color="gray.700" mb={2}>
                                        🚀 Inicio del Servicio
                                      </Text>
                                      <Text>{service.serviceStart ? new Date(service.serviceStart).toLocaleString('es-ES') : 'No iniciado'}</Text>
                                    </Box>
                                    <Box>
                                      <Text fontWeight="semibold" color="gray.700" mb={2}>
                                        🏁 Fin del Servicio
                                      </Text>
                                      <Text>{service.serviceEnd ? new Date(service.serviceEnd).toLocaleString('es-ES') : 'En curso'}</Text>
                                    </Box>
                                  </SimpleGrid>

                                  <Divider />

                                  {/* Observaciones del servicio */}
                                  {service.observations && service.observations.trim() !== '' && (
                                    <Box>
                                      <Text fontWeight="semibold" color="gray.700" mb={2}>
                                        💬 Observaciones Generales del Servicio
                                      </Text>
                                      <Box p={3} bg="gray.50" borderRadius="md" borderLeft="4px" borderLeftColor="gray.400">
                                        <Text fontSize="sm">{service.observations}</Text>
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Observaciones del cliente */}
                                  {service.customerObservations && service.customerObservations.trim() !== '' && (
                                    <Box>
                                      <Text fontWeight="semibold" color="gray.700" mb={2}>
                                        👤 Petición/Observaciones del Cliente
                                      </Text>
                                      <Box p={3} bg="gray.50" borderRadius="md" borderLeft="4px" borderLeftColor="gray.400">
                                        <Text fontSize="sm">{service.customerObservations}</Text>
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Observaciones del mecánico */}
                                  {service.mechanicObservation && service.mechanicObservation.trim() !== '' && (
                                    <Box>
                                      <Text fontWeight="semibold" color="gray.700" mb={2}>
                                        {Number(service.serviceTypeId) === 2 ||
                                        String(service.serviceType?.name || '')
                                          .toLowerCase()
                                          .includes('garant')
                                          ? 'Observaciones finales'
                                          : '🔧 Observaciones del Mecánico'}
                                      </Text>
                                      <Box p={3} bg="gray.50" borderRadius="md" borderLeft="4px" borderLeftColor="gray.400">
                                        <Text fontSize="sm">{service.mechanicObservation}</Text>
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Observaciones de cancelación */}
                                  {service.canceledObservation && service.canceledObservation.trim() !== '' && (
                                    <Box>
                                      <Text fontWeight="semibold" color="red.700" mb={2}>
                                        ❌ Motivo de Cancelación
                                      </Text>
                                      <Box p={3} bg="red.50" borderRadius="md" borderLeft="4px" borderLeftColor="red.400">
                                        <Text fontSize="sm" color="red.700">{service.canceledObservation}</Text>
                                      </Box>
                                    </Box>
                                  )}

                                  <Divider />

                                  <Accordion allowMultiple reduceMotion>
                                    <SchedulerCollapsibleSection title="Productos utilizados">
                                      {service.usedProducts?.length > 0 ? (
                                        <Table variant="simple" size="sm" w="auto">
                                          <Thead>
                                            <Tr>
                                              <Th>Código</Th>
                                              <Th>Descripción</Th>
                                              <Th isNumeric>Cantidad</Th>
                                            </Tr>
                                          </Thead>
                                          <Tbody>
                                            {service.usedProducts.map((product) => (
                                              <Tr key={product.id || product.productId}>
                                                <Td fontSize="sm">{product.product?.code || '—'}</Td>
                                                <Td fontSize="sm">
                                                  {product.productName || product.product?.name || '—'}
                                                </Td>
                                                <Td fontSize="sm" isNumeric>
                                                  {Number(product.quantity) || 1}
                                                </Td>
                                              </Tr>
                                            ))}
                                          </Tbody>
                                        </Table>
                                      ) : (
                                        <Text fontSize="sm" color="gray.500">
                                          —
                                        </Text>
                                      )}
                                    </SchedulerCollapsibleSection>
                                  </Accordion>

                                  <Divider />

                                  {/* Eventos del servicio — vista compacta */}
                                  {!(
                                    Number(service.serviceTypeId) === 2 ||
                                    String(service.serviceType?.name || '')
                                      .toLowerCase()
                                      .includes('garant')
                                  ) &&
                                  (service.vehicleServiceEventsHistories && service.vehicleServiceEventsHistories.length > 0) && (
                                    <Box>
                                      <Text fontWeight="semibold" color="gray.700" mb={2} fontSize="sm">
                                        Eventos ({service.vehicleServiceEventsHistories.length})
                                      </Text>
                                      <Box
                                        borderWidth="1px"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        maxH="220px"
                                        overflowY="auto"
                                        overflowX="auto"
                                        overscrollBehavior="contain"
                                        data-scrollable="true"
                                      >
                                        <Table size="sm" variant="simple">
                                          <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                                            <Tr>
                                              <Th w="56px" py={1}>Cód.</Th>
                                              <Th py={1}>Descripción</Th>
                                              <Th w="72px" py={1}>Estado</Th>
                                              <Th w="64px" py={1}>Rep.</Th>
                                              <Th py={1}>Nota</Th>
                                            </Tr>
                                          </Thead>
                                          <Tbody>
                                            {[...(service.vehicleServiceEventsHistories || [])]
                                              .sort((a, b) =>
                                                String(a.event?.code || '').localeCompare(
                                                  String(b.event?.code || ''),
                                                  undefined,
                                                  { numeric: true },
                                                ),
                                              )
                                              .map((eventHistory, eventIndex) => {
                                                const isOk =
                                                  eventHistory.status === 'Ok' ||
                                                  eventHistory.completed;
                                                const isNoOk = eventHistory.status === 'NoOk';
                                                const comment =
                                                  eventHistory.comment &&
                                                  String(eventHistory.comment).trim() !== '' &&
                                                  String(eventHistory.comment).trim() !== '0'
                                                    ? String(eventHistory.comment)
                                                    : '';
                                                return (
                                                  <Tr
                                                    key={eventHistory.id || eventIndex}
                                                    bg={
                                                      isOk
                                                        ? 'green.50'
                                                        : isNoOk
                                                          ? 'red.50'
                                                          : undefined
                                                    }
                                                  >
                                                    <Td py={1} fontSize="xs" color="gray.500" fontWeight="bold">
                                                      {eventHistory.event?.code || '—'}
                                                    </Td>
                                                    <Td py={1} fontSize="xs">
                                                      {eventHistory.eventName ||
                                                        eventHistory.event?.name ||
                                                        '—'}
                                                    </Td>
                                                    <Td py={1}>
                                                      <Badge
                                                        colorScheme={
                                                          isOk
                                                            ? 'green'
                                                            : isNoOk
                                                              ? 'red'
                                                              : 'gray'
                                                        }
                                                        fontSize="0.65rem"
                                                      >
                                                        {eventHistory.status === 'Ok' || isOk
                                                          ? 'OK'
                                                          : eventHistory.status === 'NoOk'
                                                            ? 'nOk'
                                                            : '—'}
                                                      </Badge>
                                                    </Td>
                                                    <Td py={1} fontSize="xs">
                                                      {eventHistory.reparado ? 'Sí' : '—'}
                                                    </Td>
                                                    <Td py={1} fontSize="xs" color="gray.600" maxW="180px">
                                                      {comment || '—'}
                                                    </Td>
                                                  </Tr>
                                                );
                                              })}
                                          </Tbody>
                                        </Table>
                                      </Box>
                                    </Box>
                                  )}

                                  <Divider />

                                  {/* Información de facturación */}
                                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    <Box>
                                      <Text fontWeight="semibold" color="gray.700" mb={2}>
                                        🧾 Información de Facturación
                                      </Text>
                                      <VStack align="start" spacing={1}>
                                        <Text fontSize="sm">
                                          <strong>Factura ID:</strong> {service.invoiceId || 'Sin factura'}
                                        </Text>
                                        <Text fontSize="sm">
                                          <strong>Documento ID:</strong> {service.documentId || 'Sin documento'}
                                        </Text>
                                        <Text fontSize="sm">
                                          <strong>Transfer ID:</strong> {service.customerTransferId || 'N/A'}
                                        </Text>
                                      </VStack>
                                    </Box>

                                    <Box>
                                      <Text fontWeight="semibold" color="gray.700" mb={2}>
                                        📊 Información del Sistema
                                      </Text>
                                      <VStack align="start" spacing={1}>
                                        <Text fontSize="sm">
                                          <strong>ID Servicio:</strong> #{service.id}
                                        </Text>
                                        <Text fontSize="sm">
                                          <strong>Creado:</strong>{' '}
                                          {service.createdAt
                                            ? formatDateOnlyDisplay(service.createdAt)
                                            : 'N/A'}
                                        </Text>
                                        <Text fontSize="sm">
                                          <strong>Actualizado:</strong>{' '}
                                          {service.updatedAt
                                            ? formatDateOnlyDisplay(service.updatedAt)
                                            : 'N/A'}
                                        </Text>
                                      </VStack>
                                    </Box>
                                  </SimpleGrid>

                                  {/* Cliente del servicio */}
                                  {service.customer && (
                                    <Box>
                                      <Text fontWeight="semibold" color="gray.700" mb={2}>
                                        👤 Información del Cliente
                                      </Text>
                                      <Box p={3} bg="gray.50" borderRadius="md" borderLeft="4px" borderLeftColor="gray.400">
                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                                          <Text fontSize="sm">
                                            <strong>Nombre:</strong> {service.customer.name}
                                          </Text>
                                          <Text fontSize="sm">
                                            <strong>Documento:</strong> {service.customer.document}
                                          </Text>
                                          <Text fontSize="sm">
                                            <strong>Teléfono:</strong> {service.customer.phone}
                                          </Text>
                                          <Text fontSize="sm">
                                            <strong>Email:</strong> {service.customer.email}
                                          </Text>
                                        </SimpleGrid>
                                      </Box>
                                    </Box>
                                  )}

                                </VStack>
                              </Box>
                            </Collapse>
                          </Box>
                        ))}
                    </>
                  ) : (
                    <Box textAlign="center" py={12} bg="gray.50" borderRadius="lg">
                      <Text fontSize="6xl" mb={4}>🔧</Text>
                      <Text color="gray.500" fontSize="xl" fontWeight="semibold">
                        No hay servicios registrados
                      </Text>
                      <Text color="gray.400" fontSize="md" mt={2}>
                        Este vehículo aún no tiene historial de servicios
                      </Text>
                    </Box>
                  )}
                </VStack>
              </TabPanel>

              {/* Tab 3: Historial de Clientes */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  {displayVehicle.vehicleCustomerHistories && displayVehicle.vehicleCustomerHistories.length > 0 ? (
                    <>
                      <Box textAlign="center" mb={4}>
                        <Badge colorScheme="gray" fontSize="lg" p={2}>
                          {displayVehicle.vehicleCustomerHistories.length} propietarios registrados
                        </Badge>
                      </Box>
                      
                      {[...(displayVehicle.vehicleCustomerHistories || [])]
                        .sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0))
                        .map((history, index) => (
                          <Box
                            key={`customer-${index}`}
                            p={5}
                            borderWidth="1px"
                            borderRadius="lg"
                            bg="gray.50"
                            borderColor="gray.200"
                            shadow="sm"
                          >
                            <HStack justify="space-between" align="start" mb={3}>
                              <VStack align="start" spacing={2}>
                                <Text fontWeight="bold" fontSize="lg" color="purple.800">
                                  👤 {safeText(history.customer, 'Cliente sin nombre')}
                                </Text>
                                
                                <VStack align="start" spacing={1}>
                                  <Text fontSize="md" color="gray.600">
                                    📅 <strong>Desde:</strong>{' '}
                                    {history.startDate
                                      ? formatDateOnlyLong(history.startDate, 'Sin fecha')
                                      : 'Sin fecha'}
                                  </Text>
                                  
                                  {history.endDate && (
                                    <Text fontSize="md" color="gray.600">
                                      📅 <strong>Hasta:</strong>{' '}
                                      {formatDateOnlyLong(history.endDate)}
                                    </Text>
                                  )}
                                </VStack>

                                {history.customer?.email && (
                                  <Text fontSize="sm" color="gray.600">
                                    📧 {safeText(history.customer.email)}
                                  </Text>
                                )}
                                
                                {history.customer?.phone && (
                                  <Text fontSize="sm" color="gray.600">
                                    📞 {safeText(history.customer.phone)}
                                  </Text>
                                )}
                              </VStack>
                              
                              {!history.endDate && (
                                <Badge colorScheme="gray" fontSize="md" p={2}>
                                  ✅ Propietario Actual
                                </Badge>
                              )}
                            </HStack>
                          </Box>
                        ))}
                    </>
                  ) : (
                    <Box textAlign="center" py={12} bg="gray.50" borderRadius="lg">
                      <Text fontSize="6xl" mb={4}>👥</Text>
                      <Text color="gray.500" fontSize="xl" fontWeight="semibold">
                        No hay historial de clientes
                      </Text>
                      <Text color="gray.400" fontSize="md" mt={2}>
                        Este vehículo no tiene propietarios registrados
                      </Text>
                    </Box>
                  )}
                </VStack>
              </TabPanel>

            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default VehicleHistoryModalComplete;